<svelte:options
  customElement={{
    tag: "oc-carousel-v1",
    shadow: "none",
    /*                                            */
    extend: window.__components.extend({ delegateFocus: true }),
    props: {
      arrowButtonPosition: { type: "String", attribute: "arrow-button-position" },
    },
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import PaginationDotsV1 from "../../../common/components/PaginationDotsV1/PaginationDotsV1.svelte";
  import type { Props } from "./CarouselV1.types";

  export let host: HTMLElement;

  export let arrowButtonPosition: Exclude<Props["arrowButtonPosition"], undefined>;

  let currentIndex = 0;

  $: slides = host.childElementCount;
  let carouselStage: HTMLElement;

  /*                                                        */
  let hideLeftArrow = true;
  let hideRightArrow = true;

  let leftButton: boolean = false;
  let rightButton: boolean = false;
  let leftButtonTimer: number | undefined = undefined;
  let rightButtonTimer: number | undefined = undefined;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          currentIndex = Array.from(host.children).indexOf(entry.target);
        }

        clearTimeout(leftButtonTimer);
        clearTimeout(rightButtonTimer);

        if (currentIndex === 0) {
          if (leftButton === true) {
            hideLeftArrow = true;
            leftButtonTimer = setTimeout(async () => {
              leftButton = false;
            }, 1000) as unknown as number;
          }
        } else {
          leftButton = true;
          hideLeftArrow = false;
        }

        if (currentIndex === slides - 1) {
          hideRightArrow = true;
          rightButtonTimer = setTimeout(() => {
            rightButton = false;
          }, 1000) as unknown as number;
        } else {
          rightButton = true;
          hideRightArrow = false;
        }
      });
    },
    { threshold: 0.5 },
  );

  onMount(() => {
    slides = host.childElementCount;

    /*                 */
    Array.from(host.children).forEach((child) => {
      observer.observe(child);
    });
  });

  const moveSlide = (direction: "left" | "right") => {
    if (direction === "left") {
      if (currentIndex <= slides - 1) {
        host.children[currentIndex - 1]?.scrollIntoView();
      }
    } else if (direction === "right") {
      if (currentIndex >= 0 && currentIndex !== slides - 1) {
        host.children[currentIndex + 1]?.scrollIntoView();
      }
    }
  };
</script>

<div class="carousel">
  <div class="carousel__stage" id="carousel-stage" bind:this={carouselStage}>
    <slot />
  </div>
  <oc-icon-button-v1
    class="carousel__arrow carousel__arrow--left"
    class:carousel__arrow--hidden={hideLeftArrow}
    class:carousel__arrow--removed={leftButton === false}
    style="--arrow-button-position:{arrowButtonPosition}"
    iconType="arrow-left"
    on:click={() => moveSlide("left")}
    aria-hidden="true"
    tabindex="-1"
    elevated
  ></oc-icon-button-v1>
  <oc-icon-button-v1
    class="carousel__arrow carousel__arrow--right"
    class:carousel__arrow--hidden={hideRightArrow}
    class:carousel__arrow--removed={rightButton === false}
    style="--arrow-button-position:{arrowButtonPosition}"
    iconType="arrow-right"
    on:click={() => moveSlide("right")}
    aria-hidden="true"
    tabindex="-1"
    elevated
  ></oc-icon-button-v1>
</div>
<PaginationDotsV1 amountDots={slides} activeDot={currentIndex} {carouselStage} />

<style lang="scss" global>
  @use "../../../common/scss/mixins/mixins.scss" as *;
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "../../../common/scss/mixins/visually-hidden.scss" as *;

  /*                                         */
  ::slotted(*) {
    scroll-snap-align: start;
    scroll-snap-stop: always;
    flex: 0 0 100%;
    height: 100%;
  }

  ::slotted(.oc-card-v1.oc-card-v1--interactive):before {
    outline-offset: -2px !important;
  }

  .carousel {
    display: flex;
    position: relative;

    &__stage {
      display: flex;
      gap: tokens.$oc-component-carousel-stage-gap-x;
      flex: 0 0 100%;
      overflow-x: auto;
      scrollbar-width: none; /*     */
      scroll-snap-type: x mandatory;

      /*                                    */
      @supports (scroll-snap-type: x mandatory) {
        scroll-snap-type: x mandatory;
      }

      /*                                    */
      @supports (scroll-behavior: smooth) {
        scroll-behavior: smooth;
      }

      /*                                    */
      @supports (scroll-behavior: smooth) and (scroll-snap-type: x mandatory) {
        scroll-behavior: smooth;
        scroll-snap-type: x mandatory;
      }

      /*                                            */
      @media (prefers-reduced-motion) {
        scroll-behavior: auto;
      }

      &::-webkit-scrollbar {
        display: none; /*                */
      }
    }

    /*          */
    @media (hover: none) and (pointer: coarse) {
      &__arrow {
        display: none;
      }
    }

    /*                                                 */
    @media (hover: hover) and (pointer: fine), (hover: none) and (pointer: fine) {
      &__arrow {
        position: absolute;
        top: calc(
          var(--arrow-button-position, 50%) - tokens.$oc-component-icon-button-50-background-size /
            2
        );

        &--hidden {
          /*                          */
          /*                  */
          opacity: 0;
        }

        &--removed {
          @include visually-hidden();
          /*                     */
          /*          */
        }

        &--left {
          left: 8px;
        }

        &--right {
          right: 8px;
        }
      }
    }
  }
</style>
